import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import { FiArrowLeft, FiCornerUpLeft, FiCornerUpRight, FiPlay, FiMail, FiSliders, FiCheck, FiZap, FiAlertCircle, FiEdit3, FiGlobe, FiRefreshCw } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { Template } from '../../../model/Template';
import { TemplateSampleResult } from '../../../model/GetTemplatesResult';
import api from '../../../api/api';
import { to, fillTemplatePreview } from '../../../util/util';
import { toast } from '../Toast';
import { previewPath, welcomeTemplateName, sendTemplateTest, welcomeMessageDetail } from './EmailTemplates';
import { SendTestModal } from './CampaignModals';

const suggestions = ['Make the button our brand colour', 'Make the header logo bigger', 'Tighten the spacing'];

// spec 2:55902, deliberately different (user decision 2026-10-12): the canvas is a live preview of the whole template and
// edits are instructions AI applies to the HTML, so there is no block builder. Used for is read-only. Last edited omitted (no data).
// Undo / Redo walk the HTML versions of this visit (original + each AI change, rebuild or code edit). Send a Test sends the saved template.
const TemplateBuilder: React.FC<{ templateId: string }> = ({ templateId }) => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const user = useSelector((state: IGlobalState) => state.user);
    const [testing, setTesting] = useState(false);
    const [device, setDevice] = useState<'Computer' | 'Phone'>('Computer');
    const [view, setView] = useState<'Preview' | 'Code'>('Preview');
    const [codeDraft, setCodeDraft] = useState<string | null>(null);
    const [instruction, setInstruction] = useState('');
    const [frameHeight, setFrameHeight] = useState(600);
    const [template, setTemplate] = useState<Template | null>(null);
    const [isAnnouncement, setIsAnnouncement] = useState(false);
    const [versions, setVersions] = useState<string[]>([]);
    const [versionIndex, setVersionIndex] = useState(0);
    const html = versions[versionIndex] ?? '';
    const [changing, setChanging] = useState(false);
    const [rebuilding, setRebuilding] = useState(false);
    const busy = changing || rebuilding;
    const mounted = useRef(true);
    const [failedChecks, setFailedChecks] = useState<string[]>([]);
    const [savedHtml, setSavedHtml] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [sample, setSample] = useState<TemplateSampleResult>({});
    const dirty = useRef(false);
    dirty.current = template != null && html !== savedHtml;

    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        setTemplate(null);
        setError('');
        const [err, data] = await to(api().getTemplate(stock.id, templateId));
        if (!isMounted()) return;
        if (err) { setError((err as any)?.response?.status === 404 ? `That template doesn't belong to ${stock.companyName || stock.ticker}` : err.message || 'Failed to load the template'); return; }
        setTemplate(data.template);
        setIsAnnouncement(data.isAnnouncementTemplate);
        setVersions([data.template.html]);
        setVersionIndex(0);
        setSavedHtml(data.template.html);
    }, [stock?.id, templateId]);

    // lorem until it arrives; may wait on the AI the first time (see TemplatesController.GetTemplateSampleResult)
    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        setSample({});
        const [, result] = await to(api().getTemplateSample(stock.id));
        if (isMounted() && result) setSample(result);
    }, [stock?.id]);

    useEffect(() => {
        const warn = (e: BeforeUnloadEvent) => { if (dirty.current) e.preventDefault(); };
        window.addEventListener('beforeunload', warn);
        return () => { window.removeEventListener('beforeunload', warn); mounted.current = false; };
    }, []);

    const fitFrame = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
        const body = e.currentTarget.contentDocument?.body;
        if (body) setFrameHeight(body.scrollHeight);
    };

    const back = <button type="button" className="link back" onClick={() => history.push('/communications/email-templates')}><FiArrowLeft /> Templates</button>;
    if (error) return <div className="announcement-preview email-builder template-builder">{back}<p className="error">{error}</p></div>;
    if (!template) return <div className="announcement-preview email-builder template-builder">{back}<p className="loading">Loading template…</p></div>;

    const save = async () => {
        setSaving(true);
        const [err, result] = await to(api().saveTemplate({ ...template, html }));
        setSaving(false);
        if (err || !result.success) { toast.error(`Save failed: ${err?.message || result?.errorMessage}`); return; }
        setSavedHtml(html);
        toast.success('Template saved');
    };

    const addVersion = (newHtml: string) => {
        setVersions([...versions.slice(0, versionIndex + 1), newHtml]);
        setVersionIndex(versionIndex + 1);
    };

    const makeChange = async () => {
        setChanging(true);
        setFailedChecks([]);
        const [err, result] = await to(api().aiEditTemplate({ stockId: stock.id, html, instruction }));
        setChanging(false);
        if (err || !result.html) {
            setFailedChecks(result?.failedChecks ?? []);
            toast.error(err?.message || result?.errorMessage || 'The change failed');
            return;
        }
        addVersion(result.html);
        setInstruction('');
    };

    // a code edit becomes one Undo step when the box loses focus
    const commitCode = () => {
        if (codeDraft != null && codeDraft !== html) addVersion(codeDraft);
        setCodeDraft(null);
    };

    // background job (site fetch + AI, a minute or two); polled every 5 s until it finishes or the page is left
    const rebuild = async () => {
        setRebuilding(true);
        setFailedChecks([]);
        const [err, started] = await to(api().rebuildTemplate(stock.id, templateId));
        let status = started;
        while (!err && status?.status === 'running' && mounted.current) {
            await new Promise(r => setTimeout(r, 5000));
            const [pollErr, polled] = await to(api().getTemplateRebuildStatus(stock.id));
            if (pollErr) { toast.error(pollErr.message); break; }
            status = polled;
        }
        if (!mounted.current) return;
        setRebuilding(false);
        if (status?.status === 'done' && status.templateId === templateId && status.html) { addVersion(status.html); return; }
        if (err || status?.status === 'failed' || status?.errorMessage) toast.error(err?.message || status?.errorMessage || 'The rebuild failed');
    };

    const isWelcome = template.name === welcomeTemplateName;
    const roles: [string, boolean][] = [
        ['ASX announcements', isAnnouncement],
        ['Welcome email', isWelcome],
        ['Notifications', false],
        ['Campaigns only', template.isForCustomCampaign],
    ];
    const usedFor = isAnnouncement ? 'Default for announcement emails' : isWelcome ? 'Sent when you invite a holder' : template.isForCustomCampaign ? 'For campaigns you write yourself' : 'Not used yet';
    const savedText = saving ? 'Saving…' : html === savedHtml ? 'All changes saved' : 'Unsaved changes';

    // same rules the server enforces on every AI change and save (EmailTemplateChecks); live while the code is being typed
    const checked = codeDraft ?? html;
    const placeholders = Array.from(new Set(template.html.match(/\{\{\s*[\w.]+\s*\}\}/g) ?? []));
    const checks: [string, boolean][] = [
        ['Unsubscribe link present', checked.includes('{{list_unsubscribe}}')],
        ['Placeholders kept', placeholders.every(p => checked.includes(p))],
        ['Safe for every inbox (no scripts, SVG, iframes or forms)', !/<(script|svg|iframe|form)\b/i.test(checked)],
        ['Complete template', /<\/html>\s*$/i.test(checked)],
    ];

    return <div className="announcement-preview email-builder template-builder">
        {back}
        <div className="title-row">
            <div>
                <h1>{template.name}</h1>
                <div className="meta"><span>{savedText} · {usedFor}</span></div>
            </div>
            <div className="actions">
                <button type="button" className="btn-outline" disabled={busy || versionIndex === 0} onClick={() => setVersionIndex(versionIndex - 1)}><FiCornerUpLeft /> Undo</button>
                <button type="button" className="btn-outline" disabled={busy || versionIndex === versions.length - 1} onClick={() => setVersionIndex(versionIndex + 1)}><FiCornerUpRight /> Redo</button>
                <button type="button" className="btn-outline" onClick={() => history.push(previewPath(templateId))}><FiPlay /> Preview</button>
                <button type="button" className="btn-outline" disabled={html !== savedHtml} title={html !== savedHtml ? 'Save to send a test' : undefined} onClick={() => setTesting(true)}><FiMail /> Send a Test</button>
                <button type="button" className="btn-primary" onClick={save} disabled={html === savedHtml || saving}>{saving ? 'Saving…' : 'Save Template'}</button>
            </div>
        </div>

        {testing && <SendTestModal defaultTo={user.username} sub="The saved template, filled with your newest announcement" hideAddTest onClose={() => setTesting(false)} onSend={addresses => sendTemplateTest(stock, templateId, addresses)} />}

        <div className="card used-for">
            <span className="label-row"><FiSliders /> Used for</span>
            <div className="chips">
                {roles.map(([label, on]) => <span key={label} className={on ? 'on' : ''}>{on && <FiCheck />} {label}</span>)}
            </div>
        </div>

        <div className="builder">
            <div className="canvas">
                <div className="toggles">
                    <div className="device">
                        {(['Preview', 'Code'] as const).map(v => <button key={v} type="button" className={v === view ? 'active' : ''} onClick={() => setView(v)}>{v}</button>)}
                    </div>
                    {view === 'Preview' && <div className="device">
                        {(['Computer', 'Phone'] as const).map(d => <button key={d} type="button" className={d === device ? 'active' : ''} onClick={() => setDevice(d)}>{d}</button>)}
                    </div>}
                </div>
                {view === 'Preview'
                    ? <div className={`frame ${device.toLowerCase()}`} key={device}>
                        <iframe title="Template" className="template-part" srcDoc={fillTemplatePreview(html, stock.ticker, sample, sample.summary, true)} sandbox="allow-same-origin" style={{ height: frameHeight }} onLoad={fitFrame} />
                    </div>
                    : <textarea className="code" spellCheck={false} disabled={busy} value={codeDraft ?? html} onChange={e => setCodeDraft(e.target.value)} onBlur={commitCode} />}
            </div>

            <div className="right">
                {template.templateType && <div className="card rebuild">
                    <h2><FiGlobe /> First, brand it from your website</h2>
                    <p className="hint">A one-off first step: Investi takes your website's colours and logo and applies them to this layout. Do it before making other changes — it replaces the whole template (Undo brings it back). Takes a minute or two.</p>
                    <button type="button" className="btn-outline" disabled={busy} onClick={rebuild}><FiRefreshCw /> {rebuilding ? 'Rebuilding from website…' : 'Rebuild from website'}</button>
                </div>}
                <div className="card ai">
                    <h2><FiZap /> Change it</h2>
                    <p className="hint">Say what to change and Investi rewrites the template for you, keeping it safe for every inbox.</p>
                    <textarea rows={4} disabled={busy} placeholder="e.g. Make the button green and add more space around the headline" value={instruction} onChange={e => setInstruction(e.target.value)} />
                    <div className="suggestions">
                        {suggestions.map(s => <button key={s} type="button" disabled={busy} onClick={() => setInstruction(s)}>{s}</button>)}
                    </div>
                    <button type="button" className="btn-primary" disabled={!instruction.trim() || busy} onClick={makeChange}><FiEdit3 /> {changing ? 'Making the change…' : 'Make the Change'}</button>
                </div>
                <div className="card checklist">
                    <h2>Checks</h2>
                    <ul>{checks.map(([text, ok]) => <li key={text} className={ok ? 'ok' : ''}><span className="tick">{ok && <FiCheck />}</span>{text}</li>)}</ul>
                    {failedChecks.length > 0 && <div className="failed">
                        <p>The last change was not applied:</p>
                        <ul>{failedChecks.map(f => <li key={f}>{f}</li>)}</ul>
                    </div>}
                    <p className="hint">Every change is checked before you can save it.</p>
                    {isWelcome && <p className="hint"><code>{'{{welcome_message}}'}</code>: {welcomeMessageDetail}</p>}
                </div>
                <div className="warn-note"><FiAlertCircle /> Saving changes every future email that uses this template. Emails already sent stay as they were.</div>
            </div>
        </div>
    </div>;
};

export default TemplateBuilder;
